import {
  DTF_ART_SPACING,
  DTF_PRINT_WIDTH,
  WHATSAPP_NUMBER,
} from "@/config/dtf";

export const parseBr = (value) => {
  if (value === null || value === undefined) return NaN;
  const raw = String(value).trim().replace(/\./g, "").replace(",", ".");
  if (raw === "") return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
};

export const formatBrl = (n) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number.isFinite(n) ? n : 0);

export const formatNum = (n, digits = 2) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(n) ? n : 0);

const EPS = 1e-9;

// Quantas peças de `across` cm cabem lado a lado na largura do filme
const perRowFor = (across, filmWidth, gap) =>
  Math.floor((filmWidth + gap) / (across + gap) + EPS);

// Orientações possíveis de uma arte dentro da faixa do filme
const orientations = (w, h, filmWidth) =>
  [
    { across: w, down: h, rotated: false },
    { across: h, down: w, rotated: true },
  ].filter((o) => o.across <= filmWidth + EPS);

const chooseOrientation = (w, h, mode, filmWidth, gap) => {
  const opts = orientations(w, h, filmWidth);
  if (!opts.length) return null;
  if (mode === "maxAcross")
    return [...opts].sort((a, b) => b.across - a.across || a.down - b.down)[0];
  if (mode === "minAcross")
    return [...opts].sort((a, b) => a.across - b.across || a.down - b.down)[0];
  // "efficiency": menor comprimento consumido por peça
  return [...opts]
    .map((o) => ({
      ...o,
      cost: o.down / Math.max(1, perRowFor(o.across, filmWidth, gap)),
    }))
    .sort((a, b) => a.cost - b.cost || a.down - b.down)[0];
};

// Encaixe em faixas (shelf packing) dentro da largura fixa do filme.
// Peças ordenadas por comprimento decrescente e alocadas na primeira
// faixa onde ainda sobra largura.
const packShelves = (pieces, filmWidth, gap) => {
  const sorted = [...pieces].sort((a, b) => b.down - a.down);
  const shelves = [];
  for (const p of sorted) {
    let placed = false;
    for (const s of shelves) {
      const need = s.used === 0 ? p.across : s.used + gap + p.across;
      if (need <= filmWidth + EPS) {
        s.used = need;
        s.height = Math.max(s.height, p.down);
        s.count += 1;
        placed = true;
        break;
      }
    }
    if (!placed) shelves.push({ used: p.across, height: p.down, count: 1 });
  }
  const length =
    shelves.reduce((sum, s) => sum + s.height, 0) +
    gap * Math.max(0, shelves.length - 1);
  return { length, shelves };
};

// Fallback analítico para quantidades muito grandes (evita travar a UI)
const packByRows = (items, filmWidth, gap) => {
  let length = 0;
  let rows = 0;
  items.forEach((item) => {
    const o = chooseOrientation(
      item.width,
      item.height,
      "efficiency",
      filmWidth,
      gap,
    );
    const perRow = Math.max(1, perRowFor(o.across, filmWidth, gap));
    const r = Math.ceil(item.quantity / perRow);
    rows += r;
    length += r * o.down + gap * Math.max(0, r - 1);
  });
  return { length, shelves: new Array(rows).fill({ height: 0 }) };
};

export const calculateDtf = ({
  arts,
  pricePerMeter,
  filmWidth = DTF_PRINT_WIDTH,
  gap = DTF_ART_SPACING,
}) => {
  const items = [];
  const errors = [];

  arts.forEach((art, index) => {
    const w = parseBr(art.width);
    const h = parseBr(art.height);
    const q = parseBr(art.quantity);
    const filled = [art.width, art.height, art.quantity].some(
      (v) => String(v ?? "").trim() !== "",
    );
    if (!filled) return;

    if (!(w > 0) || !(h > 0) || !(q >= 1)) {
      errors.push({
        index,
        message: `Arte ${index + 1}: informe largura, altura e quantidade válidas.`,
      });
      return;
    }
    if (!orientations(w, h, filmWidth).length) {
      errors.push({
        index,
        message: `Arte ${index + 1}: esta arte excede a largura máxima de impressão de ${formatNum(filmWidth, 0)} cm.`,
      });
      return;
    }

    items.push({
      index,
      width: w,
      height: h,
      quantity: Math.floor(q),
      areaCm2: w * h * Math.floor(q),
    });
  });

  const quantity = items.reduce((s, i) => s + i.quantity, 0);

  let lengthCm = 0;
  let rows = 0;

  if (items.length) {
    if (quantity > 1500) {
      const r = packByRows(items, filmWidth, gap);
      lengthCm = r.length;
      rows = r.shelves.length;
    } else {
      const pieces = [];
      const best = ["efficiency", "minAcross", "maxAcross"]
        .map((mode) => {
          pieces.length = 0;
          items.forEach((item) => {
            const o = chooseOrientation(
              item.width,
              item.height,
              mode,
              filmWidth,
              gap,
            );
            for (let i = 0; i < item.quantity; i += 1) {
              pieces.push({ across: o.across, down: o.down });
            }
          });
          return packShelves(pieces, filmWidth, gap);
        })
        .sort((a, b) => a.length - b.length)[0];
      lengthCm = best.length;
      rows = best.shelves.length;
    }
  }

  const areaCm2 = items.reduce((s, i) => s + i.areaCm2, 0);
  const meters = lengthCm / 100;
  const price = parseBr(pricePerMeter);
  const hasPrice = price > 0;
  const total = hasPrice ? meters * price : null;

  return {
    valid: items.length > 0 && lengthCm > 0,
    errors,
    items,
    quantity,
    rows,
    lengthCm,
    meters,
    filmWidth,
    areaM2: areaCm2 / 10000,
    usage: lengthCm > 0 ? areaCm2 / (lengthCm * filmWidth) : 0,
    pricePerMeter: hasPrice ? price : null,
    total,
    unitPrice: hasPrice && quantity > 0 ? total / quantity : null,
  };
};

export const whatsappLink = (message) =>
  `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encodeURIComponent(message)}` : ""}`;

export const buildQuoteMessage = (result, filmWidth = DTF_PRINT_WIDTH) => {
  if (!result?.valid) {
    return "Olá! Fiz uma simulação no ClickDTF e gostaria de encomendar minha impressão DTF têxtil.";
  }
  const lines = [
    "Olá! Fiz uma simulação no ClickDTF (DTF têxtil).",
    "",
    ...result.items.map(
      (i) =>
        `Estampa ${i.index + 1}: ${formatNum(i.width)} x ${formatNum(i.height)} cm — ${i.quantity} un.`,
    ),
    `Largura útil do filme: ${formatNum(filmWidth, 0)} cm`,
    `Comprimento estimado de DTF têxtil: ${formatNum(result.lengthCm, 1)} cm (${formatNum(result.meters)} m lineares)`,
  ];
  if (result.total !== null) {
    lines.push(
      `Valor por metro linear: ${formatBrl(result.pricePerMeter)}`,
      `Valor estimado: ${formatBrl(result.total)}`,
    );
  }
  lines.push("", "Gostaria de fazer meu pedido.");
  return lines.join("\n");
};
